import React from 'react';

import { checkEsignatureIntegration } from '~/api/integrations';
import { EsignatureTokenStatusValue } from '~/features/ticket/types/esignature';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useAsync } from '~/hooks/useAsync';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useIntl } from 'react-intl';
import { api } from '~/redux/api/createApi';
import { useRouting } from '~/routing/useRouting';
import Alert from '~/ui/components/Alert/Alert';
import { testIsAdmin } from '~/utils/user';

function ESignatureSetupAlert() {
  const { navigate } = useRouting();
  const intl = useIntl();
  const currentUser = useCurrentUser();
  const clientId = currentUser.client;
  const isEsignatureV2RevampEnabled = useFlag(FlagType.EsignatureV2Revamp);

  const {
    response: hasLegacyEsignatureIntegration,
    isLoading: isLoadingLegacyEsignatureIntegration,
  } = useAsync(
    checkEsignatureIntegration,
    {},
    { condition: !isEsignatureV2RevampEnabled },
  );
  const {
    data: tokenStatus,
    isLoading: isLoadingEsignTokenStatus,
    error: esignTokenStatusError,
  } = api.endpoints.getTokenStatus.useQuery(
    {},
    {
      skip: !isEsignatureV2RevampEnabled,
    },
  );
  const isLoading = isEsignatureV2RevampEnabled
    ? isLoadingEsignTokenStatus
    : isLoadingLegacyEsignatureIntegration;

  // if unintialized and company type, admin needs to finish setup,
  // but if we get token status back for personal, it means the integration has been setup, and the user might just need to sign in themselves (so we don't show this alert)
  const hasCompanyIntegration =
    tokenStatus?.integrationType === 'company' &&
    tokenStatus.accountToken !== EsignatureTokenStatusValue.Uninitialized;
  const hasPersonalIntegration = tokenStatus?.integrationType === 'personal';
  const hasEsignIntegration =
    isEsignatureV2RevampEnabled &&
    (hasCompanyIntegration || hasPersonalIntegration);

  if (isLoading || hasLegacyEsignatureIntegration || hasEsignIntegration) {
    return null;
  }

  const actions = [];
  if (testIsAdmin(currentUser)) {
    actions.push({
      text: intl.formatMessage({
        id: 'ESignatureSetupAlert.setupProvidersButton',
        defaultMessage: 'Set up e-signature providers',
        description: 'Button text to set up e-signature providers',
      }),
      onClick: () => {
        navigate(`/admin/console/${clientId}/integrations`);
      },
    });
  }

  const alertMessage = esignTokenStatusError
    ? intl.formatMessage(
        {
          id: 'ESignatureSetupAlert.errorRetrievingTokenStatus',
          defaultMessage:
            'There was a problem retrieving your e-signature integration. Please contact your admin for further assistance. {errorDetails}',
          description:
            'Message indicating a problem retrieving the e-signature integration',
        },
        {
          errorDetails: esignTokenStatusError.message,
        },
      )
    : intl.formatMessage({
        id: 'ESignatureSetupAlert.noProviderMessage',
        defaultMessage:
          'This account has not set up an e-signature provider yet.',
        description: 'Message indicating no e-signature provider is set up',
      });

  return (
    <Alert actions={actions} variant="info">
      {alertMessage}
    </Alert>
  );
}

export default ESignatureSetupAlert;
